import React from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

// Styled-components copied from AdminHomePage.js
const SideSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  order: 1;
  @media (max-width: 1100px) {
    order: 2;
  }
`;
const ProfileCard = styled.div`
  background-color: var(--background);
  border-radius: 16px;
  overflow: hidden;
  box-shadow: var(--box-shadow);
  transition: background-color 0.3s;
`;
const ProfileHeader = styled.div`
  padding: 1rem;
  text-align: center;
`;
const AvatarContainer = styled.div`
  margin-bottom: 1rem;
`;
const Avatar = styled.img`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid var(--primary);
`;
const ProfileInfo = styled.div`
  h2 {
    font-size: 1.25rem;
    font-weight: 600;
    margin-bottom: 0.25rem;
    color: var(--text-dark);
  }
`;
const Role = styled.div`
  font-size: 0.875rem;
  color: var(--primary);
  font-weight: 500;
  margin-bottom: 0.25rem;
`;
const QuickAccessCard = styled.div`
  background-color: var(--background);
  border-radius: 16px;
  padding: 1.5rem;
  box-shadow: var(--box-shadow);
  transition: background-color 0.3s;
  h3 {
    font-size: 1.125rem;
    font-weight: 600;
    margin-bottom: 1rem;
    padding-bottom: 0.5rem;
    border-bottom: 1px solid var(--border);
    color: var(--text-dark);
  }
`;
const QuickAccessList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;
const QuickAccessItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem;
  border-radius: 8px;
  cursor: pointer;
  transition: background-color 0.2s, transform 0.2s;
  span {
    font-size: 0.938rem;
    font-weight: 500;
    color: var(--text-dark);
  }
  &:hover {
    background-color: var(--background-light);
    transform: translateX(3px);
  }
`;
const QuickAccessIcon = styled.div`
  font-size: 1.25rem;
  color: ${props => props.color || 'var(--primary)'};
`;
const AccordionGroup = styled.div`
  margin-bottom: 0.5rem;
`;
const AccordionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem;
  border-radius: 8px;
  cursor: pointer;
  background: var(--background-light);
  transition: background 0.2s;
  user-select: none;
  &:hover {
    background: var(--background);
  }
`;
const AccordionIcon = styled.div`
  font-size: 1.25rem;
`;
const AccordionChevron = styled.div`
  margin-left: auto;
  font-size: 0.85rem;
  color: #888;
  transition: transform 0.2s;
  transform: rotate(${props => props.open ? '0deg' : '-90deg'});
`;
const AccordionContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  padding-left: 0.5rem;
  margin-bottom: 0.25rem;
`;

const AdminSidebar = ({
  user,
  accordionState,
  setAccordionState,
  onSidebarToggle
}) => {
  const navigate = useNavigate();
  // Fallbacks for accordion state if not provided
  const [suratIjinOpen, setSuratIjinOpen] = React.useState(accordionState?.suratIjinOpen ?? false);
  const [laporanOpen, setLaporanOpen] = React.useState(accordionState?.laporanOpen ?? false);
  const [masterDataOpen, setMasterDataOpen] = React.useState(accordionState?.masterDataOpen ?? false);
  const [utilitasOpen, setUtilitasOpen] = React.useState(accordionState?.utilitasOpen ?? false);

  // Allow parent to control accordion state if provided
  React.useEffect(() => {
    if (accordionState && setAccordionState) {
      setSuratIjinOpen(accordionState.suratIjinOpen);
      setLaporanOpen(accordionState.laporanOpen);
      setMasterDataOpen(accordionState.masterDataOpen);
      setUtilitasOpen(accordionState.utilitasOpen);
    }
  }, [accordionState]);

  const handleAccordion = (key) => {
    if (setAccordionState) {
      setAccordionState({
        ...accordionState,
        [key]: !accordionState[key],
      });
    } else {
      if (key === 'suratIjinOpen') setSuratIjinOpen(o => !o);
      if (key === 'laporanOpen') setLaporanOpen(o => !o);
      if (key === 'masterDataOpen') setMasterDataOpen(o => !o);
      if (key === 'utilitasOpen') setUtilitasOpen(o => !o);
    }
  };
  
  // Function to handle navigation with scroll to top
  const handleNavigation = (path) => {
    navigate(path);
    window.scrollTo(0, 0);
    // Close sidebar on mobile after navigation
    if (onSidebarToggle) {
      onSidebarToggle();
    }
  };

  const avatarUrl = user?.avatarUrl || '';
  const name = user?.name || '';
  const position = user?.position || '';

  return (
    <SideSection>
      <ProfileCard>
        <ProfileHeader>
          <AvatarContainer>
            <Avatar src={avatarUrl} alt={name} />
          </AvatarContainer>
          <ProfileInfo>
            <h2>{name}</h2>
            <Role style={{ textTransform: 'capitalize' }}>{position}</Role>
          </ProfileInfo>
        </ProfileHeader>
      </ProfileCard>
      <QuickAccessCard>
        <h3>Pilih Menu</h3>
        <QuickAccessList>
          <QuickAccessItem onClick={() => handleNavigation('/admin')}>
            <QuickAccessIcon color="#1d4ed8">🏠</QuickAccessIcon>
            <span>Dashboard</span>
          </QuickAccessItem>
          
          {/* Add the new Lapor KTS menu item */}
          <QuickAccessItem onClick={() => handleNavigation('/admin/lapor-kts')}>
            <QuickAccessIcon color="#dc2626">📝</QuickAccessIcon>
            <span>Lapor KTS</span>
          </QuickAccessItem>
          
          <QuickAccessItem onClick={() => handleNavigation('/admin/ijin-interupsi')}>
            <QuickAccessIcon color="#16a34a">⏰</QuickAccessIcon>
            <span>Ijin Interupsi</span>
          </QuickAccessItem>
          
          <QuickAccessItem onClick={() => handleNavigation('/admin/laporan-kts')}>
            <QuickAccessIcon color="#e67700">📋</QuickAccessIcon>
            <span>Laporan Ketidaksesuaian</span>
          </QuickAccessItem>
          <QuickAccessItem onClick={() => handleNavigation('/admin/konseling')}>
            <QuickAccessIcon color="#2ec27e">🤝</QuickAccessIcon>
            <span>Konseling BK</span>
          </QuickAccessItem>
          <AccordionGroup>
            <AccordionHeader onClick={() => handleAccordion('suratIjinOpen')}>
              <AccordionIcon open={suratIjinOpen}>📂</AccordionIcon>
              <span>Surat Ijin</span>
              <AccordionChevron open={suratIjinOpen}>▼</AccordionChevron>
            </AccordionHeader>
            {suratIjinOpen && (
              <AccordionContent>
                <QuickAccessItem onClick={() => handleNavigation('/admin/surat-ijin')} style={{paddingLeft: 32}}>
                  <QuickAccessIcon color="#3b82f6">📝</QuickAccessIcon>
                  <span>Surat Ijin Interupsi</span>
                </QuickAccessItem>
                <QuickAccessItem onClick={() => handleNavigation('/admin/surat-ijin-tidakmasuk')} style={{paddingLeft: 32}}>
                  <QuickAccessIcon color="#e67700">📄</QuickAccessIcon>
                  <span>Pemberitahuan Tidak Masuk</span>
                </QuickAccessItem>
              </AccordionContent>
            )}
          </AccordionGroup>
          <AccordionGroup>
            <AccordionHeader onClick={() => handleAccordion('laporanOpen')}>
              <AccordionIcon open={laporanOpen}>📑</AccordionIcon>
              <span>Laporan</span>
              <AccordionChevron open={laporanOpen}>▼</AccordionChevron>
            </AccordionHeader>
            {laporanOpen && (
              <AccordionContent>
                <QuickAccessItem onClick={() => handleNavigation('/admin/laporan-lembur')} style={{paddingLeft: 32}}>
                  <QuickAccessIcon color="#7c3aed">📝</QuickAccessIcon>
                  <span>Laporan Lembur</span>
                </QuickAccessItem>
                <QuickAccessItem onClick={() => handleNavigation('/admin/laporan-kersos')} style={{paddingLeft: 32}}>
                  <QuickAccessIcon color="#7c3aed">🏞️</QuickAccessIcon>
                  <span>Laporan Kersos</span>
                </QuickAccessItem>
                <QuickAccessItem onClick={() => handleNavigation('/admin/laporan-psm')} style={{paddingLeft: 32}}>
                  <QuickAccessIcon color="#7c3aed">🎖️</QuickAccessIcon>
                  <span>Laporan PSM</span>
                </QuickAccessItem>
              </AccordionContent>
            )}
          </AccordionGroup>
          <QuickAccessItem onClick={() => handleNavigation('/admin/surat-peringatan')}>
            <QuickAccessIcon color="#dc2626">⚠️</QuickAccessIcon>
            <span>Surat Peringatan</span>
          </QuickAccessItem>
          <AccordionGroup>
            <AccordionHeader onClick={() => handleAccordion('masterDataOpen')}>
              <AccordionIcon open={masterDataOpen}>🗂️</AccordionIcon>
              <span>Master Data</span>
              <AccordionChevron open={masterDataOpen}>▼</AccordionChevron>
            </AccordionHeader>
            {masterDataOpen && (
              <AccordionContent>
                <QuickAccessItem onClick={() => handleNavigation('/admin/manajemen-siswa')} style={{paddingLeft: 32}}>
                  <QuickAccessIcon color="#3354a5">👨‍🎓</QuickAccessIcon>
                  <span>Manajemen Siswa</span>
                </QuickAccessItem>
                <QuickAccessItem onClick={() => handleNavigation('/admin/manajemen-guru')} style={{paddingLeft: 32}}>
                  <QuickAccessIcon color="#2ec27e">👨‍🏫</QuickAccessIcon>
                  <span>Manajemen Guru</span>
                </QuickAccessItem>
                <QuickAccessItem onClick={() => handleNavigation('/admin/daftar-pelanggaran')} style={{paddingLeft: 32}}>
                  <QuickAccessIcon color="#e53935">🚫</QuickAccessIcon>
                  <span>Daftar Pelanggaran</span>
                </QuickAccessItem>
                <QuickAccessItem onClick={() => handleNavigation('/admin/daftar-apresiasi')} style={{paddingLeft: 32}}>
                  <QuickAccessIcon color="#1976d2">🎉</QuickAccessIcon>
                  <span>Daftar Apresiasi</span>
                </QuickAccessItem>
              </AccordionContent>
            )}
          </AccordionGroup>
        
          <QuickAccessItem onClick={() => handleNavigation('/admin/pengumuman')}>
            <QuickAccessIcon color="#ff6b35">📢</QuickAccessIcon>
            <span>Pengumuman</span>
          </QuickAccessItem>
          <QuickAccessItem onClick={() => handleNavigation('/admin/stok-obat')}>
            <QuickAccessIcon color="#00bcd4">💊</QuickAccessIcon>
            <span>Stok Obat</span>
          </QuickAccessItem>
          <QuickAccessItem onClick={() => handleNavigation('/admin/daftar-doa')}>
            <QuickAccessIcon color="#ff9800">🙏</QuickAccessIcon>
            <span>Daftar Doa</span>
          </QuickAccessItem>
          <QuickAccessItem onClick={() => handleNavigation('/admin/users')}>
            <QuickAccessIcon color="#3b82f6">👤</QuickAccessIcon>
            <span>Admin User</span>
          </QuickAccessItem>
          <QuickAccessItem onClick={() => handleNavigation('/admin/login-log')}>
            <QuickAccessIcon color="#8b5cf6">📊</QuickAccessIcon>
            <span>Login Log</span>
          </QuickAccessItem>
          <AccordionGroup>
            <AccordionHeader onClick={() => handleAccordion('utilitasOpen')}>
              <AccordionIcon open={utilitasOpen}>🛠️</AccordionIcon>
              <span>Utilitas</span>
              <AccordionChevron open={utilitasOpen}>▼</AccordionChevron>
            </AccordionHeader>
            {utilitasOpen && (
              <AccordionContent>
                <QuickAccessItem onClick={() => handleNavigation('/admin/utilitas/ubah-status-ditolak')} style={{paddingLeft: 32}}>
                  <QuickAccessIcon color="#dc2626">🔄</QuickAccessIcon>
                  <span>Ubah Status Ditolak</span>
                </QuickAccessItem>
              </AccordionContent>
            )}
          </AccordionGroup>
        </QuickAccessList>
      </QuickAccessCard>
    </SideSection>
  );
};

export default AdminSidebar;
